import React, { useEffect, useRef } from 'react';
import * as PIXI from 'pixi.js';
import { Spine } from '@esotericsoftware/spine-pixi-v8';

const CONFIG = {
  SPINE_PATHS: {
    JSON:    '/spin/spine/Datum_Emotion.json',
    ATLAS:   '/spin/spine/Datum_Emotion.atlas',
    TEXTURE: '/spin/spine/Datum_Emotion.png',
  },
  APP: {
    RESOLUTION: 1,
    AUTO_DENSITY: false,
  },
  SKELETON: {
    DEFAULT_WIDTH:  928,
    DEFAULT_HEIGHT: 782,
    OFFSET_X: -475,
    OFFSET_Y: 0,
    INIT_DELAY: 100,
  },
  ANIM: {
    IDLE: 'animation',   // '1' is empty; 'animation' has face/eye/mouth keyframes
  },
};

// Position the spine so the face (top of bounding box) fills the container from y=0
const setupSpinePosition = (spine, containerWidth, containerHeight) => {
  const { DEFAULT_WIDTH, DEFAULT_HEIGHT, OFFSET_X, OFFSET_Y } = CONFIG.SKELETON;
  const skeletonInfo = spine.skeleton.data.skeleton || {};
  const width   = skeletonInfo.width  || DEFAULT_WIDTH;
  const height  = skeletonInfo.height || DEFAULT_HEIGHT;
  const offsetX = skeletonInfo.x != null ? skeletonInfo.x : OFFSET_X;
  const offsetY = skeletonInfo.y != null ? skeletonInfo.y : OFFSET_Y;

  // Scale so the character fills the container height (0.94 to avoid bottom clip)
  const scale = (containerHeight * 0.90) / height;
  spine.scale.set(scale);

  // Center horizontally (bounding box center ≈ Spine x=0)
  const centerX = offsetX + width / 2;
  spine.x = containerWidth / 2 - centerX * scale;

  // In PIXI Y-down + Spine Y-up:  PIXI_y = spine.y - spineValue * scale
  // Top of character = offsetY + height  →  should map to PIXI y=0
  spine.y = (offsetY + height) * scale;
};

const ModalBackground = () => {
  const canvasContainerRef = useRef(null);
  const appRef             = useRef(null);

  useEffect(() => {
    const container = canvasContainerRef.current;
    if (!container) return;

    let app = null;
    let isMounted = true;

    const init = async () => {
      try {
        const w = container.offsetWidth  || 360;
        const h = container.offsetHeight || 290;

        app = new PIXI.Application();
        await app.init({
          width:        w,
          height:       h,
          backgroundAlpha: 0,
          resolution:   window.devicePixelRatio || 1,
          autoDensity:  true,
        });
        app.renderer.background.alpha = 0;

        if (!isMounted || !canvasContainerRef.current) { app.destroy(); return; }

        canvasContainerRef.current.appendChild(app.canvas);
        appRef.current = app;

        app.canvas.style.position = 'absolute';
        app.canvas.style.top      = '0';
        app.canvas.style.left     = '0';
        app.canvas.style.width    = '100%';
        app.canvas.style.height   = '100%';

        await PIXI.Assets.load([
          CONFIG.SPINE_PATHS.JSON,
          CONFIG.SPINE_PATHS.ATLAS,
          CONFIG.SPINE_PATHS.TEXTURE,
        ]);

        if (!isMounted) return;

        const spine = Spine.from({
          skeleton:   CONFIG.SPINE_PATHS.JSON,
          atlas:      CONFIG.SPINE_PATHS.ATLAS,
          autoUpdate: true,
        });

        spine.state.setAnimation(0, CONFIG.ANIM.IDLE, true);

        await new Promise(resolve => setTimeout(resolve, CONFIG.SKELETON.INIT_DELAY));
        if (!isMounted) return;

        setupSpinePosition(spine, app.screen.width, app.screen.height);
        app.stage.addChild(spine);
      } catch (err) {
        console.error('ModalBackground init error:', err);
      }
    };

    init();

    return () => {
      isMounted = false;
      if (appRef.current) {
        try { appRef.current.destroy(true, { children: true, texture: true, baseTexture: true }); }
        catch (_) {}
        appRef.current = null;
      }
    };
  }, []);

  return (
    <div style={styles.wrapper}>
      <div ref={canvasContainerRef} style={styles.canvas} />
    </div>
  );
};

const styles = {
  wrapper: {
    position: 'absolute', top: -165, left: 0,
    width: '100%', height: '185px',
    overflow: 'hidden', pointerEvents: 'none', zIndex: 4,
  },
  canvas: { position: 'absolute', inset: 0, width: '100%', height: '100%' },
};

export default ModalBackground;
